import React, { Suspense } from 'react';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom';

import './App.css';

import { LanguageProvider } from './context/LanguageContext';
import Navbar from './components/layout/Navbar';
import HeroBanner from './components/layout/HeroBanner';
import Footer from './components/layout/Footer';
import InnerPageBanner from './components/layout/InnerPageBanner';
import AppealsWidget from './components/layout/AppealsWidget';
import PageLoader from './components/common/PageLoader';

const HeroFeatures = React.lazy(() => import('./components/layout/HeroFeatures'));
const HomeInfoSection = React.lazy(() => import('./components/layout/HomeInfoSection'));
const RektorMurojaati = React.lazy(() => import('./components/layout/RektorMurojaati'));
const StudentAnnouncementStats = React.lazy(() => import('./components/layout/StudentAnnouncementStats'));
const HomePartnersSection = React.lazy(() => import('./components/layout/HomePartnersSection'));
const StatisticsSection = React.lazy(() => import('./components/layout/StatisticsSection'));
const SeasonalEffectsOverlay = React.lazy(() => import('./components/layout/SeasonalEffectsOverlay'));

const PartnersPage = React.lazy(() => import('./pages/PartnersPage'));
const NewsPage = React.lazy(() => import('./pages/NewsPage'));
const NewsDetailPage = React.lazy(() => import('./pages/NewsDetailPage'));
const RektorPage = React.lazy(() => import('./pages/RektorPage'));
const FacultiesPage = React.lazy(() => import('./pages/FacultiesPage'));
const DepartmentsPage = React.lazy(() => import('./pages/DepartmentsPage'));
const SectionsPage = React.lazy(() => import('./pages/SectionsPage'));
const AnnouncementsPage = React.lazy(() => import('./pages/AnnouncementsPage'));
const AnnouncementDetailPage = React.lazy(() => import('./pages/AnnouncementDetailPage'));
const StudentsPage = React.lazy(() => import('./pages/StudentsPage'));
const StudentDetailPage = React.lazy(() => import('./pages/StudentDetailPage'));
const DynamicPage = React.lazy(() => import('./pages/DynamicPage'));
const MenuContentPage = React.lazy(() => import('./pages/MenuContentPage'));
const EmployeeProfilePage = React.lazy(() => import('./pages/EmployeeProfilePage'));
const AdministrationPage = React.lazy(() => import('./pages/AdministrationPage'));
const AcademicEntityPage = React.lazy(() => import('./pages/AcademicEntityPage'));
const AdmissionPage = React.lazy(() => import('./pages/AdmissionPage'));

// Route o'zgarganda scroll holatini boshqaradi va ayrim legacy hash route'larni
// to'g'ri sahifaga yo'naltiradi.
function ScrollManager() {
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (location.pathname === '/' && location.hash === '#news') {
      navigate('/news', { replace: true });
      return;
    }

    window.scrollTo({ top: 0, left: 0 });
  }, [location.pathname, location.hash, navigate]);

  return null;
}

function SiteLayout() {
  return (
    <>
      <ScrollManager />
      <Navbar />
      <InnerPageBanner />
      <Outlet />
      <Footer />
      <AppealsWidget />
    </>
  );
}

// Lazy yuklanadigan bo'limlar uchun vizual placeholder.
function SectionFallback({ minHeight = 320 }) {
  return <PageLoader minHeight={minHeight} />;
}

// Bosh sahifada ekrandan pastdagi bo'limlarni faqat foydalanuvchi ularga
// yaqinlashganda mount qilamiz. Shunda birinchi ekran ortiqcha API so'rovlarini kutmaydi.
function DeferredHomeSection({ children, minHeight }) {
  const containerRef = React.useRef(null);
  const [shouldRender, setShouldRender] = React.useState(false);

  React.useEffect(() => {
    const target = containerRef.current;

    if (!target || !('IntersectionObserver' in window)) {
      setShouldRender(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin: '320px 0px' }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef}>
      {shouldRender ? children : <SectionFallback minHeight={minHeight} />}
    </div>
  );
}

// Decorative effects are not needed for the first paint. Defer their large
// animation module until the browser has completed the interactive render.
function DeferredSeasonalEffects() {
  const [shouldLoad, setShouldLoad] = React.useState(false);

  React.useEffect(() => {
    const loadEffects = () => setShouldLoad(true);
    const idleCallback = window.requestIdleCallback;
    const idleId = idleCallback
      ? idleCallback(loadEffects, { timeout: 2500 })
      : window.setTimeout(loadEffects, 1200);

    return () => {
      if (idleCallback) {
        window.cancelIdleCallback(idleId);
      } else {
        window.clearTimeout(idleId);
      }
    };
  }, []);

  return shouldLoad ? (
    <Suspense fallback={null}>
      <SeasonalEffectsOverlay />
    </Suspense>
  ) : null;
}

// Bosh sahifa alohida yig'iladi:
// hero qismlar darhol, og'irroq bo'limlar esa Suspense orqali yuklanadi.
function HomePage() {
  return (
    <>
      <HeroBanner />

      <div className="app-content-shell">
        <DeferredHomeSection minHeight={180}>
          <Suspense fallback={<SectionFallback minHeight={180} />}>
            <HeroFeatures />
          </Suspense>
        </DeferredHomeSection>

        <DeferredHomeSection minHeight={520}>
          <Suspense fallback={<SectionFallback minHeight={520} />}>
            <HomeInfoSection />
          </Suspense>
        </DeferredHomeSection>

        <DeferredHomeSection minHeight={520}>
          <Suspense fallback={<SectionFallback minHeight={520} />}>
            <StudentAnnouncementStats />
          </Suspense>
        </DeferredHomeSection>

        <DeferredHomeSection minHeight={420}>
          <Suspense fallback={<SectionFallback minHeight={420} />}>
            <RektorMurojaati />
          </Suspense>
        </DeferredHomeSection>

        <DeferredHomeSection minHeight={280}>
          <Suspense fallback={<SectionFallback minHeight={280} />}>
            <HomePartnersSection />
          </Suspense>
        </DeferredHomeSection>

        <DeferredHomeSection minHeight={420}>
          <Suspense fallback={<SectionFallback minHeight={420} />}>
            <StatisticsSection />
          </Suspense>
        </DeferredHomeSection>
      </div>
    </>
  );
}

// Frontendning markaziy route yig'ish nuqtasi.
function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <DeferredSeasonalEffects />
        <Routes>
          <Route element={<SiteLayout />}>

            <Route path="/" element={<HomePage />} />

          <Route
            path="/partners"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <PartnersPage />
              </Suspense>
            }
          />

          <Route
            path="/news"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <NewsPage />
              </Suspense>
            }
          />

          <Route
            path="/news/page/:id"
            element={
              <Suspense fallback={<SectionFallback minHeight={720} />}>
                <NewsDetailPage />
              </Suspense>
            }
          />

          <Route
            path="/rektor"
            element={
              <Suspense fallback={<SectionFallback minHeight={720} />}>
                <RektorPage />
              </Suspense>
            }
          />

          <Route
            path="/mamuriyat"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <AdministrationPage />
              </Suspense>
            }
          />

          <Route
            path="/faculties"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <FacultiesPage />
              </Suspense>
            }
          />

          <Route
            path="/faculties/:id"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <AcademicEntityPage pageType="faculty" />
              </Suspense>
            }
          />

          <Route
            path="/faculties/:id/menu/:menuId"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <AcademicEntityPage pageType="faculty" />
              </Suspense>
            }
          />

          

          <Route
            path="/departments"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <DepartmentsPage />
              </Suspense>
            }
          />

          <Route
            path="/departments/:facultyId/:departmentId"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <AcademicEntityPage pageType="department" />
              </Suspense>
            }
          />

          <Route
            path="/departments/:facultyId/:departmentId/menu/:menuId"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <AcademicEntityPage pageType="department" />
              </Suspense>
            }
          />

          <Route
            path="/sections"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <SectionsPage />
              </Suspense>
            }
          />

          <Route
            path="/sections/:id"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <AcademicEntityPage pageType="section" />
              </Suspense>
            }
          />

          <Route
            path="/sections/:id/menu/:menuId"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <AcademicEntityPage pageType="section" />
              </Suspense>
            }
          />

          <Route
            path="/announcements"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <AnnouncementsPage />
              </Suspense>
            }
          />

          <Route
            path="/announcements/page/:id"
            element={
              <Suspense fallback={<SectionFallback minHeight={720} />}>
                <AnnouncementDetailPage />
              </Suspense>
            }
          />

          <Route
            path="/students"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <StudentsPage />
              </Suspense>
            }
          />

          <Route
            path="/students/:id"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <StudentDetailPage />
              </Suspense>
            }
          />

          <Route
            path="/admission"
            element={
              <Suspense fallback={<SectionFallback minHeight={720} />}>
                <AdmissionPage />
              </Suspense>
            }
          />

          <Route
            path="/pages/:id"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <DynamicPage />
              </Suspense>
            }
          />

          <Route
            path="/menu/:id"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <MenuContentPage />
              </Suspense>
            }
          />

          <Route
            path="/employees/:slug"
            element={
              <Suspense fallback={<SectionFallback minHeight={960} />}>
                <EmployeeProfilePage />
              </Suspense>
            }
          />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
